import { Table, TableBody, TableContainer } from '@mui/material';
import {
    TableCell,
    TableFooter,
    TableFooterCell,
    TableHeader,
    TableRow,
    TableSortLabel,
    TableHeaderCell
} from '@/components/shared/styles/Table';
import { FONT_WEIGHT, SMBold } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { TipsHistoryDetail } from '@/services/PaymentsServiceV2';
import { useFormatting } from '@/utils/formatting';
import { useContext } from 'react';
import { UserContext } from '@/contexts/user/UserContext';
import { UserDetailLink } from '@/pages/users/UserDetailLink';
import { getStatusView } from '@/pages/users/UsersTable';
import { useOrdering } from '@/lib/hooks/useOrdering';
import { Status } from '../users/shifts/Status';
import { useCurrentCompanyDetails } from '@/services/paymentsQuery';
import { UserRoles } from '@/models/company';
import { CheckIcon } from '@/icons/CheckIcon';

export type TipsEmployeeListProps = {
    items?: TipsHistoryDetail[];
    totalEarnedTips?: number;
    totalWorkedHours?: number;
    date?: string;
    reloadTransactions: () => void;
};

export const TipsEmployeeList = (props: TipsEmployeeListProps) => {
    const { t } = useTranslation();
    const { getBalanceLabelV2, toDecimal } = useFormatting();
    const { company, user } = useContext(UserContext);

    const { orderedItems, handleRequestSort, orderMap } = useOrdering(
        props?.items || [],
        'name'
    );

    const { data: companyData } = useCurrentCompanyDetails();

    const isSuperAdmin = user?.role === UserRoles.SuperAdmin;

    return (
        <TableContainer>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell
                            sx={{ fontWeight: 'bold', width: '24vw' }}
                        >
                            <TableSortLabel
                                direction={orderMap.name || 'asc'}
                                onClick={() => handleRequestSort('name')}
                            >
                                {t('Name')}
                            </TableSortLabel>
                        </TableHeaderCell>
                        <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                            {t('Shift')}
                        </TableHeaderCell>
                        <TableHeaderCell
                            align="center"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {t('Reported')}
                        </TableHeaderCell>
                        <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                            <TableSortLabel
                                direction={orderMap.employeeGroupName || 'asc'}
                                onClick={() =>
                                    handleRequestSort('employeeGroupName')
                                }
                            >
                                {t('Employment group')}
                            </TableSortLabel>
                        </TableHeaderCell>
                        {isSuperAdmin && (
                            <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                                {t('Status')}
                            </TableHeaderCell>
                        )}
                        <TableHeaderCell
                            align="right"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {t('Worked hours')}
                        </TableHeaderCell>
                        <TableHeaderCell
                            align="right"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {t('Share of tips')}
                        </TableHeaderCell>
                        <TableHeaderCell
                            align="right"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {t('Earned tips')}
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {orderedItems?.map((detail, index) => (
                        <TableRow
                            warning={
                                !detail.isShiftTypeExcluded &&
                                detail.shiftAvailableForPayout &&
                                detail.shiftStatus !=
                                    companyData?.data
                                        ?.shiftStatusTipsWithdrawAllowed
                            }
                            hoverEffect
                            key={JSON.stringify(detail) + index}
                        >
                            <TableCell>
                                <UserDetailLink
                                    userId={detail.userId}
                                    name={detail.name}
                                />
                            </TableCell>
                            <TableCell>
                                <Status
                                    isShiftTypeExcluded={
                                        detail.isShiftTypeExcluded
                                    }
                                    availableForPayout={
                                        detail.shiftAvailableForPayout
                                    }
                                    shiftStatusWithdrawAllowed={
                                        companyData?.data
                                            ?.shiftStatusTipsWithdrawAllowed
                                    }
                                    status={detail.shiftStatus}
                                />
                            </TableCell>
                            <TableCell align="center">
                                {detail.reportedAt && <CheckIcon />}
                                {!detail.reportedAt && <>-</>}
                            </TableCell>

                            <TableCell
                                sx={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                                component="th"
                                scope="row"
                            >
                                {detail.employeeGroupName}
                            </TableCell>

                            {isSuperAdmin && (
                                <TableCell
                                    sx={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                                >
                                    {getStatusView(
                                        detail.isFirstLogin,
                                        detail.isActive,
                                        detail.isDisabled
                                    )}
                                </TableCell>
                            )}
                            <TableCell align="right">
                                {toDecimal(detail.workedHours, 1)}
                            </TableCell>
                            <TableCell align="right">
                                {toDecimal(detail.currentShareOfTips, 2)}
                            </TableCell>

                            <TableCell
                                align="right"
                                sx={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                            >
                                {getBalanceLabelV2(
                                    detail.tipsDistributed
                                        ? detail.earnedTips
                                        : detail.estimatedTips,
                                    company.currency,
                                    { showNegativeStyle: true }
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableFooterCell>
                            <SMBold>
                                {t('total_date', { date: props.date })}
                            </SMBold>
                        </TableFooterCell>
                        <TableFooterCell></TableFooterCell>
                        <TableFooterCell></TableFooterCell>
                        <TableFooterCell></TableFooterCell>
                        {isSuperAdmin && <TableFooterCell></TableFooterCell>}
                        <TableFooterCell align="right">
                            <SMBold>
                                {toDecimal(props.totalWorkedHours, 1)}
                            </SMBold>
                        </TableFooterCell>
                        <TableFooterCell></TableFooterCell>
                        <TableFooterCell align="right">
                            <SMBold>
                                {getBalanceLabelV2(
                                    props.totalEarnedTips,
                                    company.currency,
                                    { showNegativeStyle: true }
                                )}
                            </SMBold>
                        </TableFooterCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
};
